import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { Alert, AlertTitle, Box, useTheme } from '@mui/material';
import React, { type FC, useMemo } from 'react';

import { type Event, EventCategory } from '../../models/index.js';
import { formatISO } from '../../utils/index.js';

interface EventNotificationProps {
  event: Event;
  className?: string;
  onClick?: (event: Event) => void;
  shouldShowTime?: boolean;
}

const EventNotification: FC<EventNotificationProps> = ({ event, onClick, shouldShowTime = false }) => {
  const { category, patient, start } = event;
  const theme = useTheme();
  const { icon, title, severity } = useMemo<{
    title: string;
    icon?: any;
    severity: 'error' | 'warning' | 'success' | 'info' | undefined;
  }>(() => {
    switch (category) {
      case EventCategory.EMERGENCY:
        return {
          title: 'Emergency',
          icon: <LocalHospitalIcon />,
          severity: 'error',
        };

      case EventCategory.MOTION:
        return {
          title: 'Movement detected',
          severity: 'warning',
        };

      case EventCategory.WELLNESS_CHECK:
        return {
          title: 'Wellness check',
          icon: <FavoriteBorderIcon />,
          severity: 'success',
        };
      default:
        return {
          title: 'Notification',
          severity: 'info',
        };
    }
  }, [category]);

  return (
    <Box
      sx={{ width: '100%' }}
      {...(onClick && {
        onClick: () => onClick(event),
      })}
    >
      <Alert
        severity={severity}
        icon={icon}
        variant={theme.palette.mode === 'dark' ? 'outlined' : 'standard'}
        sx={{
          px: 0.5,
          py: 0,
          fontSize: 12,
          cursor: 'pointer',
          '& .MuiAlert-action': {
            pl: 1,
            pr: 1,
            ml: 'auto',
            justifyContent: 'center',
          },
        }}
        action={shouldShowTime ? <Box>{formatISO(start, 't')}</Box> : null}
      >
        <AlertTitle sx={{ fontWeight: 'bold', fontSize: 14 }}>{title}</AlertTitle>
        {patient.firstName} {patient.lastName}
      </Alert>
    </Box>
  );
};

export default EventNotification;
